// src/components/OpportunitiesTab.jsx — v2
// Full Pursuit Workspace — 5-tab modal: Overview, People, Internal Team, Activity, AI War Room

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
import './OpportunitiesTab.css';
import AIPursuitChat from './AIPursuitChat';
import VictorDebrief from './VictorDebrief';

import PursuitIntelligenceDoc from './PursuitIntelligenceDoc';
import LogActivityModal from './LogActivityModal';

// Stage vocabulary imported from canonical source (Workstream C — Cadence Engine Foundation)
import { STAGES, ACTIVE_STAGES, CLOSED_STAGES, STAGE_CONFIG } from '../config/stages';

const PRIORITY_CONFIG = {
  High:   { color:'var(--dm-red)',bg:'var(--dm-red-light)' },
  Medium: { color:'#F97316',bg:'#FFF7ED' },
  Low:    { color:'var(--dm-grey-600)',bg:'#F3F4F6' },
};
const MEDDIC_SCORE_CONFIG = {
  '🔴 Weak — 1-2 elements':       { color:'#991B1B',bg:'var(--dm-red-light)',short:'🔴 Weak' },
  '🟡 Developing — 3-4 elements': { color:'#92400E',bg:'#FEF9C3',short:'🟡 Developing' },
  '🟢 Strong — 5-6 elements':     { color:'var(--dm-green-deep)',bg:'var(--dm-green-wash)',short:'🟢 Strong' },
};
const IMPACT_ICON = { Speed:'⚡',Risk:'🛡️',Growth:'📈',Cost:'💰' };
const INFLUENCE_CONFIG = {
  'Decision Maker':    { icon:'⭐',color:'var(--dm-red)' },
  'Economic Buyer':    { icon:'💰',color:'#DB2777' },
  'Champion':          { icon:'🏆',color:'var(--dm-indigo)' },
  'Coach':             { icon:'👤',color:'#EAB308' },
  'Sponsor':           { icon:'🎯',color:'var(--dm-green-soft)' },
  'Influencer':        { icon:'💼',color:'#A855F7' },
  'Technical Evaluator':{ icon:'🔬',color:'var(--dm-grey-600)' },
  'End User':          { icon:'👥',color:'var(--dm-grey-600)' },
  'Gatekeeper':        { icon:'🔑',color:'#F97316' },
  'Blocker':           { icon:'🚫',color:'#A855F7' },
  'Legal/Procurement': { icon:'📋',color:'var(--dm-green-soft)' },
  'Board Member':      { icon:'🏛️',color:'#F97316' },
  'Mobilizer':         { icon:'⚡',color:'var(--dm-indigo)' },
  'Skeptic':           { icon:'🤨',color:'var(--dm-red)' },
  'Neutralist':        { icon:'😐',color:'var(--dm-grey-600)' },
  'Internal Advocate': { icon:'📣',color:'var(--dm-green-soft)' },
};

// Contact type colors for pursuit team cards
const TEAM_CONTACT_COLORS = {
  'Internal Colleague': { border: '#16a34a', bg: '#f0fdf4', label: '#15803d', tag: 'Internal' },
  'Vendor Rep':         { border: 'var(--dm-blue-vivid)', bg: 'var(--dm-blue-pale)', label: 'var(--dm-blue-deep)', tag: 'Vendor' },
  'Partner':            { border: 'var(--dm-amber)', bg: 'var(--dm-amber-wash)', label: 'var(--dm-amber-deep)', tag: 'Partner' },
};

const EMPTY_FORM = {
  name:'',priority:'High',dealSize:'',probability:'10% - Prospecting',
  impactArea:'',competitiveSensitivity:'',expectedClose:'',valueHypothesis:'',
  nextMilestone:'',competitiveThreats:'',lossReason:'',meddicScore:'',
  meddicMetrics:'',meddicEconomicBuyer:'',meddicDecisionCriteria:'',
  meddicDecisionProcess:'',meddicIdentifiedPain:'',meddicChampion:'',
};

function formatDealSize(val) {
  const n = Number(val);
  if (isNaN(n)||val===''||val==null) return '-';
  if (n>=1000000) return `$${(n/1000000).toFixed(n%1000000===0?0:2)}M`;
  if (n>=1000)    return `$${(n/1000).toFixed(n%1000===0?0:1)}K`;
  return `$${n.toLocaleString()}`;
}
function meddicCompletionCount(opp) {
  return [opp.meddicMetrics,opp.meddicEconomicBuyer,opp.meddicDecisionCriteria,
    opp.meddicDecisionProcess,opp.meddicIdentifiedPain,opp.meddicChampion]
    .filter(v=>v&&v.trim()).length;
}
const isClosedStage = p => CLOSED_STAGES.includes(p);

// ── MEDDIC section (unchanged) ────────────────────────────────────────────────
function MeddicSection({ opp, isForm=false, formData, onFormChange }) {
  const [open, setOpen] = useState(false);
  const completed = isForm
    ? [formData.meddicMetrics,formData.meddicEconomicBuyer,formData.meddicDecisionCriteria,
       formData.meddicDecisionProcess,formData.meddicIdentifiedPain,formData.meddicChampion]
       .filter(v=>v&&v.trim()).length
    : meddicCompletionCount(opp||{});
  const elements = [
    { key:'meddicMetrics',label:'Metrics',icon:'📊',hint:'Quantified business impact?' },
    { key:'meddicEconomicBuyer',label:'Economic Buyer',icon:'💼',hint:'Who controls budget?' },
    { key:'meddicDecisionCriteria',label:'Decision Criteria',icon:'📋',hint:'How will they select a vendor?' },
    { key:'meddicDecisionProcess',label:'Decision Process',icon:'🗺️',hint:'Steps to signed contract?' },
    { key:'meddicIdentifiedPain',label:'Identified Pain',icon:'🎯',hint:'Core problem creating urgency?' },
    { key:'meddicChampion',label:'Champion',icon:'🏆',hint:'Who is fighting for us inside?' },
  ];
  const scoreColor = completed>=5?'var(--dm-green-deep)':completed>=3?'#92400E':'#991B1B';
  const scoreBg    = completed>=5?'var(--dm-green-wash)':completed>=3?'#FEF9C3':'var(--dm-red-light)';
  return (
    <div className="meddic-section">
      <button className="meddic-toggle" onClick={()=>setOpen(!open)}>
        <div className="meddic-toggle-left">
          <span className="meddic-label">MEDDIC Pursuit Framework</span>
          <span className="meddic-score-badge" style={{background:scoreBg,color:scoreColor}}>{completed}/6 Complete</span>
        </div>
        <span className="meddic-chevron">{open?'▲':'▼'}</span>
      </button>
      {open&&(
        <div className="meddic-body">
          <div className="meddic-progress-bar">
            <div className="meddic-progress-fill" style={{width:`${(completed/6)*100}%`,background:scoreColor}}/>
          </div>
          <div className="meddic-grid">
            {elements.map(el=>{
              const value = isForm?(formData[el.key]||''):((opp||{})[el.key]||'');
              const filled = value.trim().length>0;
              return (
                <div key={el.key} className={`meddic-element ${filled?'filled':'empty'}`}>
                  <div className="meddic-element-header">
                    <span className="meddic-icon">{el.icon}</span>
                    <span className="meddic-element-label">{el.label}</span>
                    {filled&&<span className="meddic-check">✓</span>}
                  </div>
                  {isForm?(
                    <textarea className="meddic-textarea" value={value}
                      onChange={e=>onFormChange(el.key,e.target.value)}
                      placeholder={el.hint} rows={2}/>
                  ):(
                    <p className="meddic-value">{value||<span className="meddic-empty-hint">{el.hint}</span>}</p>
                  )}
                </div>
              );
            })}
          </div>
          {isForm&&(
            <div className="meddic-score-select-row">
              <label>MEDDIC Score Override</label>
              <select value={formData.meddicScore||''} onChange={e=>onFormChange('meddicScore',e.target.value)}>
                <option value="">Auto</option>
                <option value="🔴 Weak — 1-2 elements">🔴 Weak — 1-2 elements</option>
                <option value="🟡 Developing — 3-4 elements">🟡 Developing — 3-4 elements</option>
                <option value="🟢 Strong — 5-6 elements">🟢 Strong — 5-6 elements</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Contact card for People tabs ──────────────────────────────────────────────
function ContactCard({ contact, onUnlink, onNextActionChange, nextAction, teamMember=false }) {
  const [editing, setEditing] = useState(false);
  const [localAction, setLocalAction] = useState(nextAction||'');
  const primaryLevel = (contact.influenceLevel||[])[0]||'';
  const cfg = INFLUENCE_CONFIG[primaryLevel]||{icon:'👤',color:'var(--dm-grey-600)'};
  const teamCfg = teamMember ? (TEAM_CONTACT_COLORS[contact.contact_type] || TEAM_CONTACT_COLORS['Internal Colleague']) : null;

  const handleBlur = () => {
    setEditing(false);
    if (onNextActionChange) onNextActionChange(contact.id, localAction);
  };

  return (
    <div className="opp-contact-card"
      style={{
        borderLeftColor: teamMember ? teamCfg.border : cfg.color,
        background: teamMember ? teamCfg.bg : 'var(--dm-white)',
      }}>
      <div className="occ-header">
        <span className="occ-icon">{cfg.icon}</span>
        <div className="occ-info">
          <div className="occ-name">{contact.name}</div>
          {contact.title&&<div className="occ-title">{contact.title}</div>}
        </div>
        {onUnlink&&(
          <button className="occ-unlink" onClick={()=>onUnlink(contact.id)} title="Remove from pursuit">×</button>
        )}
      </div>

      {/* Influence badges */}
      {!teamMember&&(contact.influenceLevel||[]).length>0&&(
        <div className="occ-badges">
          {contact.influenceLevel.slice(0,3).map((lvl,i)=>{
            const c = INFLUENCE_CONFIG[lvl]||{icon:'👤',color:'var(--dm-grey-600)'};
            return (
              <span key={i} className="occ-badge"
                style={{background:c.color+'20',color:c.color}}>
                {c.icon} {lvl}
              </span>
            );
          })}
        </div>
      )}

      {/* Pursuit team: type tag + role */}
      {teamMember&&(
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          {teamCfg&&(
            <span style={{
              fontSize:'10px',fontWeight:700,padding:'2px 7px',borderRadius:4,
              background: teamCfg.border+'22', color: teamCfg.label,
              textTransform:'uppercase',letterSpacing:'0.4px',
            }}>{teamCfg.tag}</span>
          )}
          {contact.roleOnPursuit&&(
            <span className="occ-role">🎯 {contact.roleOnPursuit}</span>
          )}
        </div>
      )}

      {/* Contact icons */}
      <div className="occ-actions">
        {contact.email&&<a href={`mailto:${contact.email}`} className="occ-action-link" title={contact.email}>📧</a>}
        {contact.phone&&<a href={`tel:${contact.phone}`} className="occ-action-link" title={contact.phone}>📞</a>}
        {contact.linkedin&&<a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="occ-action-link" title="LinkedIn">🔗</a>}
      </div>

      {/* Next Action (external contacts only) */}
      {!teamMember&&(
        <div className="occ-next-action">
          <div className="occ-next-label">Next Action</div>
          {editing?(
            <input
              className="occ-next-input"
              value={localAction}
              onChange={e=>setLocalAction(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={e=>e.key==='Enter'&&handleBlur()}
              placeholder="e.g. Schedule discovery call"
              autoFocus
            />
          ):(
            <div className="occ-next-value" onClick={()=>setEditing(true)}>
              {localAction||<span className="occ-next-placeholder">Click to add next action</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// MEDDIC field definitions for opening briefing (parallel to learn.js/cron's FIELDS array)
const MEDDIC_FIELDS = [
  { key: 'metrics',          label: 'Metrics',           textCol: 'meddicMetrics',           confCol: 'meddicMetricsConfidence' },
  { key: 'economic_buyer',   label: 'Economic Buyer',    textCol: 'meddicEconomicBuyer',     confCol: 'meddicEconomicBuyerConfidence' },
  { key: 'decision_criteria',label: 'Decision Criteria', textCol: 'meddicDecisionCriteria',  confCol: 'meddicDecisionCriteriaConfidence' },
  { key: 'decision_process', label: 'Decision Process',  textCol: 'meddicDecisionProcess',   confCol: 'meddicDecisionProcessConfidence' },
  { key: 'identified_pain',  label: 'Identified Pain',   textCol: 'meddicIdentifiedPain',    confCol: 'meddicIdentifiedPainConfidence' },
  { key: 'champion',         label: 'Champion',          textCol: 'meddicChampion',          confCol: 'meddicChampionConfidence' },
];

// Build Victor's opening briefing for a War Room session from already-loaded
// local data. Template-based, deterministic, no LLM call. The briefing
// orients the user on stage, MEDDIC honest qualification state (Option B
// confidence-aware), top open PID question, and recommended next action.
// Returns null if the opp is too sparse to brief (just-created hypothesis).
function buildOpeningBriefing(opp, detailData) {
  if (!opp) return null;

  const lines = [];

  // ── Stage line ──
  // Show stage + (optionally) days since last update on this opp
  const stage = opp.probability || 'Unknown stage';
  const updatedAt = detailData?.updatedAt;
  let daysStr = '';
  if (updatedAt) {
    const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
    if (days >= 0 && days < 365) daysStr = ' · last activity ' + (days === 0 ? 'today' : days + 'd ago');
  }
  lines.push('📍 **Stage:** ' + stage + daysStr);

  // ── MEDDIC line with weakest dimension callout ──
  // Use Option B confidence values when present; fall back to text-presence.
  const completeness = detailData?.meddicCompleteness ?? opp.meddicCompleteness ?? 0;
  const hasConfidence = MEDDIC_FIELDS.some(f => typeof detailData?.[f.confCol] === 'number');

  if (!hasConfidence && completeness === 100) {
    // Legacy opp — all fields have text but no Option B confidence yet
    lines.push('📊 **MEDDIC:** ' + completeness + '% (legacy — engage to refresh qualification under Option B)');
  } else if (completeness >= 70 && hasConfidence) {
    // Honest healthy qualification
    lines.push('📊 **MEDDIC:** ' + completeness + '% — qualification healthy');
  } else if (completeness === 0) {
    lines.push('📊 **MEDDIC:** fresh — qualification begins now');
  } else {
    // Find weakest dimension (lowest confidence < 40, or empty text if no confidence)
    let weakest = null;
    let weakestScore = 999;
    for (const f of MEDDIC_FIELDS) {
      const conf = detailData?.[f.confCol];
      const text = detailData?.[f.textCol] || opp[f.textCol] || '';
      const score = typeof conf === 'number' ? conf : (text.trim() ? 100 : 0);
      if (score < weakestScore) {
        weakestScore = score;
        weakest = f;
      }
    }
    if (weakest && weakestScore < 40) {
      lines.push('📊 **MEDDIC:** ' + completeness + '% · weakest: **' + weakest.label + '** (confidence ' + weakestScore + ')');
    } else {
      lines.push('📊 **MEDDIC:** ' + completeness + '%');
    }
  }

  // ── Top open question from PID ──
  const pid = detailData?.pid || {};
  const openQs = (pid.openQuestions || []).filter(q => !q.resolved);
  if (openQs.length > 0) {
    // Most recently added open question
    const latest = openQs[openQs.length - 1];
    const qText = (latest.text || '').slice(0, 140);
    if (qText) lines.push('❓ **Open question:** ' + qText);
  }

  // ── Recommended next action OR all-clear ──
  const nextAction = detailData?.recommendedNextAction;
  const nextActionAt = detailData?.nextActionUpdatedAt;
  const actionFresh = nextActionAt
    ? (Date.now() - new Date(nextActionAt).getTime()) < (7 * 86400000)
    : false;

  if (nextAction && actionFresh) {
    lines.push('💡 **Next:** ' + nextAction);
  } else if (completeness >= 70 && hasConfidence) {
    // Healthy — surface an all-clear with next milestone if available
    const milestone = detailData?.nextMilestone || opp.nextMilestone;
    if (milestone) {
      lines.push('✓ **All clear** — awaiting ' + milestone);
    } else {
      lines.push('✓ **All clear** — qualification is solid, awaiting next checkpoint');
    }
  }
  // If neither — skip the action line entirely (don't fake an action)

  if (lines.length === 0) return null;
  return lines.join('\n\n');
}

function OpportunitiesTab({ accountId, accountName='Account', onNavigate, initialOpenOppId }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [selectedOpp,   setSelectedOpp]   = useState(null);
  const [modalMode,     setModalMode]     = useState('view');  // view | edit | add
  const [modalTab,      setModalTab]      = useState('overview'); // overview|people|team|activity|ai
  const [formData,      setFormData]      = useState(EMPTY_FORM);
  const [saving,        setSaving]        = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage,    setSuccessMessage]    = useState('');
  const [addAnother,        setAddAnother]        = useState(false);
  const [debriefOpp,        setDebriefOpp]        = useState(null);

  // End Session — synchronous save+learn ref + UI state
  const chatRef = useRef(null);
  const [endingSession,     setEndingSession]     = useState(false);
  const [endSessionStatus,  setEndSessionStatus]  = useState('');

  // Detail data for the pursuit workspace
  const [detailData,      setDetailData]      = useState(null);
  const [detailContacts,  setDetailContacts]  = useState([]);
  const [detailTeam,      setDetailTeam]      = useState([]);
  const [detailLoading,   setDetailLoading]   = useState(false);
  const [accountContacts, setAccountContacts] = useState([]); // for contact picker
  const [teamMembers,     setTeamMembers]     = useState([]); // DigitalMe + vendors + partners
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactPickerTeam, setContactPickerTeam] = useState(false);
  const [teamTypeFilter,    setTeamTypeFilter]    = useState('All');
  const [notes,             setNotes]             = useState('');
  const [nextActionsMap,    setNextActionsMap]    = useState({});
  // Multiple notes state
  const [notesList,      setNotesList]      = useState([]);
  // B147 — a load failure used to be indistinguishable from an empty list.
  const [notesError,     setNotesError]     = useState('');
  const [notesLoading,   setNotesLoading]   = useState(false);
  const [showAddNote,    setShowAddNote]     = useState(false);
  const [showLogModal,    setShowLogModal]    = useState(false);
  const [newNoteTitle,   setNewNoteTitle]    = useState('');
  const [newNoteContent, setNewNoteContent]  = useState('');
  const [newNoteType,    setNewNoteType]     = useState('General');
  const [savingNote,     setSavingNote]      = useState(false);

  // Task creation state
  const [showAddTask,    setShowAddTask]     = useState(false);
  const [newTaskName,    setNewTaskName]     = useState('');
  const [newTaskPriority,setNewTaskPriority] = useState('Do Today');
  const [newTaskDue,     setNewTaskDue]      = useState('');
  const [savingTask,     setSavingTask]      = useState(false);

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await apiFetch(`/api/opportunities/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setOpportunities(data.opportunities);
        // Auto-open a specific opportunity if navigated from Pipeline,
        // Command Center Top Opps, or Dashboard — land on AI War Room since
        // a click from those surfaces implies "I want to engage this deal."
        // Local card click within OpportunitiesTab (handleCardClick) keeps
        // landing on Overview for the "quick glance" pattern.
        if (initialOpenOppId && !selectedOpp) {
          const target = data.opportunities.find(o => o.id === initialOpenOppId);
          if (target) {
            setSelectedOpp(target);
            setModalMode('view');
            setModalTab('ai');
            setDetailData(null); setDetailContacts([]); setDetailTeam([]);
          }
        }
      }
      else setError(data.error);
    } catch (err) { setError('Failed to load opportunities'); }
    finally { setLoading(false); }
  }, [accountId, initialOpenOppId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  // When selectedOpp is set (including from auto-open), load full detail
  useEffect(() => {
    if (selectedOpp && modalMode === 'view' && !detailData) {
      loadDetail(selectedOpp.id);
    }
  }, [selectedOpp]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg) => { setSuccessMessage(msg); setTimeout(()=>setSuccessMessage(''),3000); };

  // Load full detail data when opening an opp in view mode
  const loadDetail = useCallback(async (oppId) => {
    if (!oppId) return;
    setDetailLoading(true);
    try {
      const res  = await apiFetch(`/api/opportunities/detail?id=${oppId}&accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setDetailData(data.opportunity);
        setDetailContacts(data.contacts || []);
        setDetailTeam(data.pursuitTeam || []);
        setNotes(data.opportunity.notes || '');
        // Build next actions map from contacts
        const map = {};
        const contacts = data.contacts || data.opportunity?.contacts || [];
        contacts.forEach(c => {
          if (c.nextAction) map[c.id] = c.nextAction;
        });
        setNextActionsMap(map);
      }
    } catch (err) { console.error('Detail load error:', err); }
    finally { setDetailLoading(false); }
  }, [accountId]);

  // Load account contacts for the picker
  const loadAccountContacts = useCallback(async () => {
    // Reload contacts from server
    try {
      const res  = await apiFetch(`/api/contacts/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) setAccountContacts(data.contacts || []);
    } catch {}
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pursuit team candidates (always our own org / Vendor / Partner — not from the carrier account)
  const loadTeamMembers = useCallback(async () => {
    try {
      const res  = await apiFetch('/api/contacts/team-members');
      const data = await res.json();
      if (data.success) {
        setTeamMembers(data.contacts || []);
      } else {
        console.error('[team-members] API error:', data.error);
      }
    } catch (err) {
      console.error('[team-members] fetch error:', err.message);
    }
  }, []);

  const handleCardClick = (opp) => {
    setSelectedOpp(opp);
    setModalMode('view');
    setModalTab('overview');
    setDetailData(null); setDetailContacts([]); setDetailTeam([]);
    loadDetail(opp.id);
  };

  const handleAddClick = () => { setSelectedOpp(null); setFormData(EMPTY_FORM); setModalMode('add'); setModalTab('overview'); };
  const handleEditClick = (opp) => {
    setFormData({
      name:opp.name||'',priority:opp.priority||'High',dealSize:opp.dealSize||'',
      probability:opp.probability||'10% - Prospecting',impactArea:opp.impactArea||'',
      competitiveSensitivity:opp.competitiveSensitivity||'',expectedClose:opp.expectedClose||'',
      valueHypothesis:opp.valueHypothesis||'',nextMilestone:opp.nextMilestone||'',
      competitiveThreats:opp.competitiveThreats||'',lossReason:opp.lossReason||'',
      meddicScore:opp.meddicScore||'',meddicMetrics:opp.meddicMetrics||'',
      meddicEconomicBuyer:opp.meddicEconomicBuyer||'',meddicDecisionCriteria:opp.meddicDecisionCriteria||'',
      meddicDecisionProcess:opp.meddicDecisionProcess||'',meddicIdentifiedPain:opp.meddicIdentifiedPain||'',
      meddicChampion:opp.meddicChampion||'',
    });
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setSelectedOpp(null); setModalMode('view'); setModalTab('overview');
    setFormData(EMPTY_FORM); setSuccessMessage(''); setShowDeleteConfirm(false);
    setDetailData(null); setDetailContacts([]); setDetailTeam([]); setShowContactPicker(false);
  };

  const handleFormChange = (field, value) => setFormData(prev=>({...prev,[field]:value}));

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      if (modalMode==='add') {
        const res  = await apiFetch('/api/opportunities/create',{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({accountId,...formData}),
        });
        const data = await res.json();
        if (data.success) {
          await fetchOpportunities();
          showToast(`✅ "${formData.name}" created!`);
          if (addAnother) setFormData(EMPTY_FORM); else handleCloseModal();
        } else alert('Failed: '+data.error);
      } else if (modalMode==='edit') {
        const res  = await apiFetch('/api/opportunities/update',{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({id:selectedOpp.id,...formData}),
        });
        const data = await res.json();
        if (data.success) {
          await fetchOpportunities();
          showToast(`✅ "${formData.name}" updated!`);
          handleCloseModal();
          // Debrief is triggered only on Won/Lost transitions, not on Tabled.
          // Intentionally narrower than CLOSED_STAGES (which also includes 'Reviewed / Not Advancing').
          const dealClosedStates = ['100% - Deal Signed by Client','0% - Deal Lost / Tabled'];
          if (!dealClosedStates.includes(selectedOpp?.probability)&&dealClosedStates.includes(formData.probability)) {
            setTimeout(()=>setDebriefOpp({name:formData.name,notionUrl:selectedOpp?.notionUrl||''}),600);
          }
        } else alert('Failed: '+data.error);
      }
    } catch { alert('Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedOpp) return;
    setDeleting(true);
    try {
      const res  = await apiFetch(`/api/opportunities/delete?id=${selectedOpp.id}`,{method:'DELETE'});
      const data = await res.json();
      if (data.success) { await fetchOpportunities(); showToast('🗑️ Deleted'); handleCloseModal(); }
      else alert('Failed: '+data.error);
    } catch { alert('Error deleting'); }
    finally { setDeleting(false); }
  };

  // Contact linking
  const handleLinkContact = async (contactId, teamMember=false) => {
    if (!selectedOpp) return;
    try {
      await apiFetch('/api/opportunities/update-contacts',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({opportunityId:selectedOpp.id,contactId,action:'add',teamMember}),
      });
      setShowContactPicker(false);
      await loadDetail(selectedOpp.id);
      setAccountContacts([]);
      loadAccountContacts();
      showToast('✅ Contact linked');
    } catch { showToast('⚠️ Failed to link contact'); }
  };

  const handleUnlinkContact = async (contactId, teamMember=false) => {
    if (!selectedOpp) return;
    try {
      await apiFetch('/api/opportunities/update-contacts',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({opportunityId:selectedOpp.id,contactId,action:'remove',teamMember}),
      });
      await loadDetail(selectedOpp.id);
      setAccountContacts([]);
      loadAccountContacts();
      showToast('Contact removed');
    } catch {}
  };

  // Load multiple notes for this opportunity
  const loadNotes = useCallback(async (oppId) => {
    if (!oppId) return;
    setNotesLoading(true);
    try {
      const res  = await apiFetch('/api/opportunities/notes?opportunityId=' + oppId);
      // B147 — apiFetch RESOLVES on a 4xx/5xx, so reading data.success straight off
      // an error body silently produced an empty list: "No notes yet" was shown for
      // a failure exactly as it was for an opportunity with no notes.
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        setNotesError(detail.error || ('Could not load notes — HTTP ' + res.status));
        setNotesList([]);
        return;
      }
      setNotesError('');
      const data = await res.json();
      if (data.success) setNotesList(data.notes || []);
    } catch {
      setNotesError('Could not reach the server to load notes');
    }
    finally { setNotesLoading(false); }
  }, []);

  // Create a new note
  const handleCreateNote = async () => {
    if (!newNoteContent.trim() || !selectedOpp) return;
    setSavingNote(true);
    try {
      const res  = await apiFetch('/api/opportunities/notes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunityId: selectedOpp.id,
          accountId,
          title:    newNoteTitle || new Date().toLocaleDateString('en-US', {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}),
          content:  newNoteContent,
          noteType: newNoteType,
        }),
      });
      // B147 — the failure that discarded five months of typed notes. A 405 resolved,
      // data.success was undefined, nothing cleared and nothing was said. The composer
      // is NOT closed on failure: the text the user typed stays on screen so they can
      // retry or copy it out.
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        showToast('Could not save note — ' + (detail.error || ('HTTP ' + res.status)));
        return;
      }
      const data = await res.json();
      if (data.success) {
        setNewNoteTitle(''); setNewNoteContent(''); setNewNoteType('General'); setShowAddNote(false);
        loadNotes(selectedOpp.id);
        showToast('Note saved');
      } else {
        showToast('Could not save note — ' + (data.error || 'unknown error'));
      }
    } catch {
      showToast('Could not reach the server — your note has not been saved');
    }
    finally { setSavingNote(false); }
  };

  // Delete a note
  const handleDeleteNote = async (noteId) => {
    try {
      const res = await apiFetch('/api/opportunities/notes?noteId=' + noteId, { method: 'DELETE' });
      // B147 — the row was removed from the list before anything confirmed the server
      // had deleted it, so a failed delete looked successful until the next reload
      // brought the note back.
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        showToast('Could not delete note — ' + (detail.error || ('HTTP ' + res.status)));
        return;
      }
      setNotesList(prev => prev.filter(n => n.id !== noteId));
      showToast('Note deleted');
    } catch {
      showToast('Could not reach the server — the note was not deleted');
    }
  };

  // Create a task from the opportunity card
  const handleCreateTask = async () => {
    if (!newTaskName.trim() || !selectedOpp) return;
    setSavingTask(true);
    try {
      const res  = await apiFetch('/api/tasks/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:            newTaskName,
          priority:        newTaskPriority,
          dueDate:         newTaskDue || undefined,
          accountId,
          opportunityName: selectedOpp.name,
          status:          'Suggested',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNewTaskName(''); setNewTaskPriority('Do Today'); setNewTaskDue(''); setShowAddTask(false);
        loadDetail(selectedOpp.id);
        showToast('Task created');
      }
    } catch {}
    finally { setSavingTask(false); }
  };


  // Notes — handled by handleCreateNote. B147: this panel is written against a
  // multi-note collection that DOES NOT EXIST. The "Pursuit Notes DB" it was
  // built on was a Notion database dropped in the April 2026 migration; the
  // Supabase schema has one text column, opportunities.notes, which
  // api/warroom/learn.js appends to. Until B147.1 picks a data model, the GET
  // 400s and both writes 405 — see the entry before changing anything here.

  // Next action save
  const handleNextActionChange = async (contactId, action) => {
    const updated = {...nextActionsMap,[contactId]:action};
    setNextActionsMap(updated);
    if (!selectedOpp) return;
    try {
      await apiFetch('/api/opportunities/update-notes',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({opportunityId:selectedOpp.id,contactNextActions:updated}),
      });
    } catch {}
  };

  // ── Render pipeline board ────────────────────────────────────────────────
  const grouped = STAGES.reduce((acc,s)=>{acc[s]=opportunities.filter(o=>o.probability===s);return acc;},{});
  // Catch opportunities with empty or unrecognized probability — show in Under Review column
  const knownProbs = new Set(STAGES);
  const unclassified = opportunities.filter(o => !knownProbs.has(o.probability));
  if (unclassified.length > 0) grouped['Under Review'] = [...(grouped['Under Review'] || []), ...unclassified];
  const activeOpps = opportunities.filter(o=>ACTIVE_STAGES.includes(o.probability));
  const totalTCV = (() => {
    const nums = activeOpps.map(o=>Number(o.dealSize)).filter(n=>!isNaN(n)&&n>0);
    return nums.length ? formatDealSize(nums.reduce((a,b)=>a+b,0)) : null;
  })();

  if (loading) return <div className="opp-loading"><div className="spinner"/><p>Loading opportunities...</p></div>;
  if (error)   return <div className="opp-error"><p>⚠️ {error}</p></div>;

  // Contact picker available contacts (not already linked)
  // Pursuit team: from dedicated team-members endpoint (DigitalMe + vendors + partners)
  const linkedTeamIds = new Set((detailTeam).map(c=>c.id));
  const availableTeam = teamMembers.filter(c => !linkedTeamIds.has(c.id));
  // External contacts for People tab (carrier contacts minus already-linked)
  const linkedContactIds2 = new Set((detailContacts).map(c=>c.id));
  const availableExternal = accountContacts.filter(c => !linkedContactIds2.has(c.id));

  return (
    <div className="opp-container">
      {successMessage&&<div className="opp-toast">{successMessage}</div>}

      <div className="opp-header">
        <div className="opp-header-left">
          <h3>Opportunities <span className="opp-count">({opportunities.length})</span></h3>
          {activeOpps.length>0&&(
            <div className="opp-summary-pills">
              <span className="opp-pill">{activeOpps.length} Active</span>
              {totalTCV&&<span className="opp-pill">{totalTCV}</span>}
            </div>
          )}
        </div>
        <button className="btn-add-opp" onClick={handleAddClick}>+ Add Opportunity</button>
      </div>

      {opportunities.length===0?(
        <div className="opp-empty">
          <p>No opportunities yet.</p>
          <button className="btn-add-opp-large" onClick={handleAddClick}>+ Add First Opportunity</button>
        </div>
      ):(
        <div className="opp-pipeline">
          {STAGES.map(stage=>{
            const cfg=STAGE_CONFIG[stage]; const stageOpps=grouped[stage]||[];
            if (!stageOpps.length) return null;
            return (
              <div key={stage} className="opp-stage-column">
                <div className="opp-stage-header" style={{borderTopColor:cfg.color}}>
                  <span className="opp-stage-icon">{cfg.icon}</span>
                  <span className="opp-stage-label">{cfg.label}</span>
                  <span className="opp-stage-badge" style={{background:cfg.bg,color:cfg.color}}>{stageOpps.length}</span>
                </div>
                <div className="opp-cards">
                  {stageOpps.map(opp=>{
                    const mc=meddicCompletionCount(opp);
                    const mCfg=MEDDIC_SCORE_CONFIG[opp.meddicScore]||null;
                    const mColor=mc>=5?'var(--dm-green-deep)':mc>=3?'#92400E':'var(--dm-grey-600)';
                    const mBg=mc>=5?'var(--dm-green-wash)':mc>=3?'#FEF9C3':'#F3F4F6';
                    return (
                      <div key={opp.id} className="opp-card" onClick={()=>handleCardClick(opp)}>
                        {/* ── Reality Bar ────────────────────────────────── */}
                        {(opp.victor_confidence || opp.victor_read || opp.signalCount > 0) && (
                          <div className="opp-reality-bar">
                            {opp.victor_confidence && (
                              <span className={`opp-reality-conf opp-reality-conf--${(opp.victor_confidence||'').toLowerCase()}`}>
                                {opp.victor_confidence === 'High' ? '🟢' : opp.victor_confidence === 'Medium' ? '🟡' : '🔴'}
                                {' '}{opp.victor_confidence}
                              </span>
                            )}
                            {opp.signalCount > 0 && (
                              <span className="opp-reality-signals">
                                📡 {opp.signalCount} signal{opp.signalCount !== 1 ? 's' : ''}
                              </span>
                            )}
                            {opp.victor_read && (
                              <span className="opp-reality-read" title={opp.victor_read}>
                                ⚡ {opp.victor_read.length > 60 ? opp.victor_read.slice(0, 60) + '…' : opp.victor_read}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="opp-card-header">
                          <div className="opp-card-title-block">
                            {opp.accountName&&<span className="opp-card-account-inline">🏢 {opp.accountName}</span>}
                            <h4 className="opp-card-title">{opp.name}</h4>
                          </div>
                          {opp.priority&&(
                            <span className="opp-card-priority"
                              style={{background:PRIORITY_CONFIG[opp.priority]?.bg,color:PRIORITY_CONFIG[opp.priority]?.color}}>
                              {opp.priority}
                            </span>
                          )}
                        </div>
                        <div className="opp-card-meddic-row">
                          <span className="opp-meddic-pill"
                            style={{background:mCfg?mCfg.bg:mBg,color:mCfg?mCfg.color:mColor}}>
                            {mCfg?mCfg.short:`MEDDIC ${mc}/6`}
                          </span>
                        </div>
                        <div className="opp-card-grid">
                          <div className="opp-card-grid-item"><span className="opp-card-label">Stage</span>
                            <span className="opp-card-value" style={{color:STAGE_CONFIG[opp.probability]?.color,fontSize:'10px',fontWeight:600}}>
                              {STAGE_CONFIG[opp.probability]?.icon} {STAGE_CONFIG[opp.probability]?.label||'—'}
                            </span>
                          </div>
                          <div className="opp-card-grid-item"><span className="opp-card-label">Deal Size</span>
                            <span className="opp-card-value opp-deal-size">{opp.dealSize!=null?formatDealSize(opp.dealSize):'—'}</span>
                          </div>
                          <div className="opp-card-grid-item"><span className="opp-card-label">Close</span>
                            <span className="opp-card-value">{opp.expectedClose||'—'}</span>
                          </div>
                          <div className="opp-card-grid-item"><span className="opp-card-label">Impact</span>
                            <span className="opp-card-value">{opp.impactArea?`${IMPACT_ICON[opp.impactArea]} ${opp.impactArea}`:'—'}</span>
                          </div>
                        </div>
                        {opp.nextMilestone&&<div className="opp-card-next-milestone">🎯 {opp.nextMilestone}</div>}
                        {onNavigate&&(
                          <div className="opp-card-ai-bar" onClick={e=>e.stopPropagation()}>
                            <button className="opp-ai-btn opp-ai-btn--victor"
                              onClick={()=>onNavigate('victor',null,{accountId:opp.accountId||accountId,accountName:opp.accountName||accountName,opportunityId:opp.id,opportunityName:opp.name,mode:'deal_coach'})}>
                              ⚡ Victor
                            </button>
                            <button className="opp-ai-btn opp-ai-btn--cyrano"
                              onClick={()=>onNavigate('content',null,{accountId:opp.accountId||accountId,accountName:opp.accountName||accountName,opportunityId:opp.id,opportunityName:opp.name})}>
                              ✍️ Cyrano
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODAL ─────────────────────────────────────────────────────────── */}
      {(selectedOpp||modalMode==='add')&&(
        <div className="opp-modal-overlay" onClick={handleCloseModal}>
          <div className={`opp-modal ${modalMode==='view'?'opp-modal-workspace':'opp-modal-wide'}`}
            onClick={e=>e.stopPropagation()}>

            <div className="opp-modal-header">
              <h2 className="opp-modal-title">
                {modalMode==='add'?'✨ Add Opportunity':
                 modalMode==='edit'?'✏️ Edit Opportunity':
                 selectedOpp?.name||'Opportunity'}
              </h2>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {modalMode==='view'&&(
                  <button className="opp-btn-secondary" onClick={()=>handleEditClick(selectedOpp)}>Edit</button>
                )}
                <button className="opp-modal-close" onClick={handleCloseModal}>✕</button>
              </div>
            </div>

            {/* Tabs — view mode only */}
            {modalMode==='view'&&(
              <div className="opp-workspace-tabs">
                {[
                  {id:'overview', label:'📊 Overview'},
                  {id:'business_case', label:`📋 Business Case${(detailData?.businessCase||detailData?.internalBriefing)?' ●':''}`},
                  {id:'people',   label:`👥 People ${detailContacts?.length>0?`(${detailContacts.length})`:''}` },
                  {id:'team',     label:`🎯 Pursuit Team ${detailTeam?.length>0?`(${detailTeam.length})`:''}` },
                  {id:'activity', label:'📋 Activity'},
                  {id:'ai',       label:'🤖 AI War Room'},
                ].map(tab=>(
                  <button key={tab.id}
                    className={`opp-ws-tab ${modalTab===tab.id?'active':''}`}
                    onClick={()=>{
                      setModalTab(tab.id);
                      if (tab.id==='people'&&accountContacts.length===0) loadAccountContacts();
                      if (tab.id==='team') loadTeamMembers();
                      if (tab.id==='activity') loadNotes(selectedOpp?.id);
                    }}>
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div className="opp-modal-content">

              {/* ── ADD / EDIT FORM ──────────────────────────────────────── */}
              {(modalMode==='add'||modalMode==='edit')&&(
                <>
                  <div className="opp-form-grid">
                    <div className="opp-form-field full">
                      <label>Opportunity Name *</label>
                      <input type="text" value={formData.name}
                        onChange={e=>handleFormChange('name',e.target.value)}
                        placeholder="e.g., Core System Modernization"/>
                    </div>
                    <div className="opp-form-field">
                      <label>Stage *</label>
                      <select value={formData.probability} onChange={e=>handleFormChange('probability',e.target.value)}>
                        {STAGES.map(s=>(
                          <option key={s} value={s}>{STAGE_CONFIG[s].icon} {STAGE_CONFIG[s].label}{STAGE_CONFIG[s].pct!=null?` (${STAGE_CONFIG[s].pct}%)`:''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Priority</label>
                      <select value={formData.priority} onChange={e=>handleFormChange('priority',e.target.value)}>
                        <option value="">Select...</option>
                        <option>High</option><option>Medium</option><option>Low</option>
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Deal Size ($)</label>
                      <input type="number" min="0" step="10000" value={formData.dealSize||''}
                        onChange={e=>handleFormChange('dealSize',e.target.value)} placeholder="e.g. 450000"/>
                      {formData.dealSize&&!isNaN(Number(formData.dealSize))&&(
                        <span className="opp-deal-preview">{formatDealSize(formData.dealSize)}</span>
                      )}
                    </div>
                    <div className="opp-form-field">
                      <label>Expected Close</label>
                      <input type="date" value={formData.expectedClose}
                        onChange={e=>handleFormChange('expectedClose',e.target.value)}/>
                    </div>
                    <div className="opp-form-field">
                      <label>Impact Area</label>
                      <select value={formData.impactArea} onChange={e=>handleFormChange('impactArea',e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Speed">⚡ Speed</option>
                        <option value="Risk">🛡️ Risk</option>
                        <option value="Growth">📈 Growth</option>
                        <option value="Cost">💰 Cost</option>
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Competitive Sensitivity</label>
                      <select value={formData.competitiveSensitivity} onChange={e=>handleFormChange('competitiveSensitivity',e.target.value)}>
                        <option value="">Select...</option>
                        <option>High</option><option>Medium</option><option>Low</option>
                      </select>
                    </div>
                    <div className="opp-form-field full">
                      <label>🎯 Next Milestone</label>
                      <input type="text" value={formData.nextMilestone}
                        onChange={e=>handleFormChange('nextMilestone',e.target.value)}
                        placeholder="e.g., Submit proposal by March 20"/>
                    </div>
                    <div className="opp-form-field full">
                      <label>Value Hypothesis</label>
                      <textarea value={formData.valueHypothesis}
                        onChange={e=>handleFormChange('valueHypothesis',e.target.value)}
                        rows={3} placeholder="Describe the value proposition..."/>
                    </div>
                    <div className="opp-form-field full">
                      <label>Competitive Threats</label>
                      <textarea value={formData.competitiveThreats}
                        onChange={e=>handleFormChange('competitiveThreats',e.target.value)}
                        rows={2} placeholder="Who else is in the deal?"/>
                    </div>
                    {isClosedStage(formData.probability)&&(
                      <div className="opp-form-field full">
                        <label>Loss / Close Reason</label>
                        <select value={formData.lossReason} onChange={e=>handleFormChange('lossReason',e.target.value)}>
                          <option value="">Select reason...</option>
                          <option>Price</option><option>Competitor — Deloitte</option>
                          <option>Competitor — Accenture</option><option>Competitor — Other SI</option>
                          <option>No Decision</option><option>Timing</option>
                          <option>Relationship Gap</option><option>Capability Gap</option>
                          <option>Budget Eliminated</option>
                        </select>
                      </div>
                    )}
                    {modalMode==='add'&&(
                      <div className="opp-form-checkbox full">
                        <label>
                          <input type="checkbox" checked={addAnother} onChange={e=>setAddAnother(e.target.checked)}/>
                          <span>Save and add another</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="meddic-victor-notice">
                    ⚔️ MEDDIC is owned by Victor — fields are populated automatically from your Floor and War Room sessions.
                  </div>
                  <MeddicSection opp={formData}/>
                </>
              )}

              {/* ── VIEW: OVERVIEW TAB ──────────────────────────────────── */}
              {modalMode==='view'&&modalTab==='overview'&&selectedOpp&&(
                <div className="opp-overview-layout">

                  {/* ── Key Metrics Strip — 4 across ── */}
                  <div className="opp-metrics-strip">
                    <div className="opp-metric-card">
                      <div className="opp-metric-label">Stage</div>
                      <div className="opp-metric-value">
                        <span className="opp-badge opp-badge--sm"
                          style={{background:STAGE_CONFIG[selectedOpp.probability]?.bg,color:STAGE_CONFIG[selectedOpp.probability]?.color}}>
                          {STAGE_CONFIG[selectedOpp.probability]?.icon} {STAGE_CONFIG[selectedOpp.probability]?.label||selectedOpp.probability}
                        </span>
                      </div>
                    </div>
                    <div className="opp-metric-card">
                      <div className="opp-metric-label">Deal Size</div>
                      <div className="opp-metric-value opp-metric-big">{formatDealSize(selectedOpp.dealSize)}</div>
                    </div>
                    <div className="opp-metric-card">
                      <div className="opp-metric-label">Priority</div>
                      <div className="opp-metric-value">
                        {selectedOpp.priority ? (
                          <span className="opp-badge opp-badge--sm"
                            style={{background:PRIORITY_CONFIG[selectedOpp.priority]?.bg,color:PRIORITY_CONFIG[selectedOpp.priority]?.color}}>
                            {selectedOpp.priority}
                          </span>
                        ) : <span style={{color:'#94a3b8'}}>-</span>}
                      </div>
                    </div>
                    <div className="opp-metric-card">
                      <div className="opp-metric-label">Expected Close</div>
                      <div className="opp-metric-value">{selectedOpp.expectedClose||'-'}</div>
                    </div>
                  </div>

                  {/* ── Secondary metrics row ── */}
                  <div className="opp-metrics-secondary">
                    {selectedOpp.impactArea&&(
                      <div className="opp-meta-pill">
                        <span className="opp-meta-label">Impact</span>
                        <span>{IMPACT_ICON[selectedOpp.impactArea]} {selectedOpp.impactArea}</span>
                      </div>
                    )}
                    {selectedOpp.competitiveSensitivity&&(
                      <div className="opp-meta-pill">
                        <span className="opp-meta-label">Competitive</span>
                        <span>{selectedOpp.competitiveSensitivity}</span>
                      </div>
                    )}
                    {selectedOpp.nextMilestone&&(
                      <div className="opp-meta-pill opp-meta-pill--milestone">
                        <span>🎯</span>
                        <span>{selectedOpp.nextMilestone}</span>
                      </div>
                    )}
                  </div>

                  {/* ── MEDDIC collapsible ── */}
                  <div className="opp-overview-section">
                    <MeddicSection opp={selectedOpp} isForm={false}/>
                  </div>

                  {/* ── Text content fields ── */}
                  {selectedOpp.valueHypothesis&&(
                    <div className="opp-overview-section">
                      <div className="opp-section-label">Value Hypothesis</div>
                      <p className="opp-view-text">{selectedOpp.valueHypothesis}</p>
                    </div>
                  )}
                  {selectedOpp.competitiveThreats&&(
                    <div className="opp-overview-section">
                      <div className="opp-section-label">Competitive Threats</div>
                      <p className="opp-view-text">{selectedOpp.competitiveThreats}</p>
                    </div>
                  )}
                  {isClosedStage(selectedOpp.probability)&&selectedOpp.lossReason&&(
                    <div className="opp-overview-section">
                      <div className="opp-section-label">Loss / Close Reason</div>
                      <span className="opp-badge" style={{background:'var(--dm-red-light)',color:'#991B1B'}}>{selectedOpp.lossReason}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── VIEW: BUSINESS CASE TAB ──────────────────────────────── */}
              {modalMode==='view'&&modalTab==='business_case'&&selectedOpp&&(
                <PursuitIntelligenceDoc
                  opportunityId={selectedOpp.id}
                  opportunityName={selectedOpp.name}
                  initialMeddic={{
                    metrics:          detailData?.meddicMetrics          || selectedOpp.meddicMetrics          || '',
                    economicBuyer:    detailData?.meddicEconomicBuyer    || selectedOpp.meddicEconomicBuyer    || '',
                    decisionCriteria: detailData?.meddicDecisionCriteria || selectedOpp.meddicDecisionCriteria || '',
                    decisionProcess:  detailData?.meddicDecisionProcess  || selectedOpp.meddicDecisionProcess  || '',
                    identifiedPain:   detailData?.meddicIdentifiedPain   || selectedOpp.meddicIdentifiedPain   || '',
                    champion:         detailData?.meddicChampion         || selectedOpp.meddicChampion         || '',
                    score:            detailData?.meddicScore            || selectedOpp.meddicScore            || '',
                  }}
                />
              )}

              {/* ── VIEW: PEOPLE TAB (Buying Committee) ─────────────────── */}
              {modalMode==='view'&&modalTab==='people'&&(
                <div className="opp-ws-section">
                  <div className="opp-ws-section-header">
                    <h4>Buying Committee</h4>
                    <button className="opp-ws-add-btn" onClick={()=>{setShowContactPicker(true);setContactPickerTeam(false);}}>
                      + Link Contact
                    </button>
                  </div>
                  {detailLoading?(
                    <div className="opp-ws-loading">Loading contacts...</div>
                  ):detailContacts?.length===0?(
                    <div className="opp-ws-empty">
                      <p>No contacts linked to this opportunity yet.</p>
                      <p style={{fontSize:'12px',color:'#64748b'}}>Link contacts from this account to track your buying committee.</p>
                      <button className="opp-ws-add-btn-lg" onClick={()=>{setShowContactPicker(true);setContactPickerTeam(false);}}>
                        + Link First Contact
                      </button>
                    </div>
                  ):(
                    <div className="opp-contacts-grid">
                      {(detailContacts).map(c=>(
                        <ContactCard key={c.id} contact={c}
                          onUnlink={(id)=>handleUnlinkContact(id,false)}
                          nextAction={nextActionsMap[c.id]||''}
                          onNextActionChange={handleNextActionChange}/>
                      ))}
                    </div>
                  )}

                  {/* Contact picker */}
                  {showContactPicker&&!contactPickerTeam&&(
                    <div className="opp-contact-picker">
                      <div className="ocp-header">
                        <h5>Link Contact to this Opportunity</h5>
                        <button onClick={()=>setShowContactPicker(false)}>×</button>
                      </div>
                      {availableExternal.length===0?(
                        <p className="ocp-empty">All contacts are already linked, or no contacts exist for this account.</p>
                      ):(
                        <div className="ocp-list">
                          {availableExternal.map(c=>(
                            <button key={c.id} className="ocp-item" onClick={()=>handleLinkContact(c.id,false)}>
                              <span className="ocp-name">{c.name}</span>
                              {c.title&&<span className="ocp-title">{c.title}</span>}
                              {c.department&&<span className="ocp-dept">{c.department}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── VIEW: INTERNAL TEAM TAB ──────────────────────────────── */}
              {modalMode==='view'&&modalTab==='team'&&(
                <div className="opp-ws-section">
                  <div className="opp-ws-section-header">
                    <h4>Pursuit Team</h4>
                    <button className="opp-ws-add-btn" onClick={()=>{loadTeamMembers();setShowContactPicker(true);setContactPickerTeam(true);setTeamTypeFilter('All');}}>
                      + Add Team Member
                    </button>
                  </div>
                  {detailLoading?(
                    <div className="opp-ws-loading">Loading team...</div>
                  ):detailTeam?.length===0?(
                    <div className="opp-ws-empty">
                      <p>No internal team members assigned yet.</p>
                      <button className="opp-ws-add-btn-lg" onClick={()=>{setShowContactPicker(true);setContactPickerTeam(true);loadTeamMembers();setTeamTypeFilter('All');}}>
                        + Add First Team Member
                      </button>
                    </div>
                  ):(
                    <div className="opp-contacts-grid">
                      {(detailTeam).map(c=>(
                        <ContactCard key={c.id} contact={c} teamMember={true}
                          onUnlink={(id)=>handleUnlinkContact(id,true)}/>
                      ))}
                    </div>
                  )}

                  {showContactPicker&&contactPickerTeam&&(
                    <div className="opp-contact-picker">
                      <div className="ocp-header">
                        <h5>Add Team Member</h5>
                        <button onClick={()=>{ setShowContactPicker(false); setTeamTypeFilter('All'); }}>×</button>
                      </div>
                      {/* ── Type filter pills ── */}
                      <div style={{ display:'flex', gap:6, padding:'6px 12px 4px', flexWrap:'wrap' }}>
                        {['All','Internal','Vendor','Partner'].map(f=>{
                          const active = teamTypeFilter === f;
                          const cfg = f==='Internal' ? TEAM_CONTACT_COLORS['Internal Colleague']
                                    : f==='Vendor'   ? TEAM_CONTACT_COLORS['Vendor Rep']
                                    : f==='Partner'  ? TEAM_CONTACT_COLORS['Partner']
                                    : null;
                          return (
                            <button key={f} onClick={()=>setTeamTypeFilter(f)} style={{
                              fontSize:'11px', fontWeight:700, padding:'3px 10px', borderRadius:12,
                              border: active ? '1.5px solid '+(cfg?.border||'var(--dm-grey-600)') : '1.5px solid #e5e7eb',
                              background: active ? (cfg ? cfg.border+'22' : '#f3f4f6') : 'var(--dm-white)',
                              color: active ? (cfg?.label||'var(--dm-grey-800)') : 'var(--dm-grey-600)',
                              cursor:'pointer', transition:'all .15s',
                            }}>{f}</button>
                          );
                        })}
                      </div>
                      {availableTeam.length===0?(
                        <p className="ocp-empty">No team members available. Add Internal Colleague, Vendor Rep, or Partner contacts to this account first.</p>
                      ):(()=>{
                        const typeMap = { Internal:'Internal Colleague', Vendor:'Vendor Rep', Partner:'Partner' };
                        const filtered = teamTypeFilter==='All'
                          ? availableTeam
                          : availableTeam.filter(c => c.contact_type === typeMap[teamTypeFilter]);
                        return filtered.length===0 ? (
                          <p className="ocp-empty">No {teamTypeFilter.toLowerCase()} contacts available to add.</p>
                        ) : (
                          <div className="ocp-list">
                            {filtered.map(c=>{
                              const tc = TEAM_CONTACT_COLORS[c.contact_type];
                              return (
                                <button key={c.id} className="ocp-item" onClick={()=>handleLinkContact(c.id,true)}>
                                  <span className="ocp-name">{c.name}</span>
                                  {c.title&&<span className="ocp-title">{c.title}</span>}
                                  {tc&&(
                                    <span style={{
                                      fontSize:'10px',fontWeight:700,padding:'2px 6px',borderRadius:4,marginLeft:'auto',
                                      background:tc.border+'22',color:tc.label,
                                    }}>{tc.tag}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* ── VIEW: ACTIVITY TAB ───────────────────────────────────── */}
              {modalMode==='view'&&modalTab==='activity'&&(
                <div className="opp-ws-section">

                  {/* ── Notes — multiple, unlimited ─────────────────────── */}
                  <div className="opp-ws-subsection">
                    <div className="opp-ws-section-header">
                      <h4>Notes ({notesList.length})</h4>
                      <button className="opp-ws-add-btn" onClick={()=>setShowAddNote(true)}>+ Add Note</button>
                    </div>

                    {/* Add note form */}
                    {showAddNote&&(
                      <div className="opp-add-note-form">
                        <div className="opp-note-form-row">
                          <input className="opp-note-title-input" value={newNoteTitle}
                            onChange={e=>setNewNoteTitle(e.target.value)}
                            placeholder="Note title (optional)"/>
                          <select className="opp-note-type-select" value={newNoteType}
                            onChange={e=>setNewNoteType(e.target.value)}>
                            {['Strategy','Meeting Debrief','Intel','Action','General'].map(t=>(
                              <option key={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                        <textarea className="opp-notes-textarea" value={newNoteContent}
                          onChange={e=>setNewNoteContent(e.target.value)}
                          placeholder="Enter note content... No character limit."
                          rows={4} autoFocus/>
                        <div className="opp-note-form-actions">
                          <button className="opp-btn-secondary" onClick={()=>{setShowAddNote(false);setNewNoteContent('');setNewNoteTitle('');}}>
                            Cancel
                          </button>
                          <button className="opp-btn-primary" onClick={handleCreateNote}
                            disabled={savingNote||!newNoteContent.trim()}>
                            {savingNote?'Saving...':'Save Note'}
                          </button>
                        </div>
                      </div>
                    )}

                    {notesError&&(
                      <div className="opp-ws-error" style={{
                        color:'var(--dm-red)', fontSize:'13px', padding:'8px 10px',
                        background:'var(--dm-red-wash)', borderRadius:'6px', marginBottom:'8px',
                      }}>⚠️ {notesError}</div>
                    )}

                    {notesLoading?<div className="opp-ws-loading">Loading notes...</div>:
                    notesList.length===0&&!showAddNote?(
                      <div className="opp-ws-empty">
                        <p>No notes yet. Add strategic notes, meeting debriefs, or intel captures.</p>
                        <button className="opp-ws-add-btn-lg" onClick={()=>setShowAddNote(true)}>+ Add First Note</button>
                      </div>
                    ):(
                      <div className="opp-notes-list">
                        {notesList.map(note=>(
                          <div key={note.id} className="opp-note-card">
                            <div className="opp-note-header">
                              <div className="opp-note-meta">
                                <span className={`opp-note-type opp-note-type--${(note.noteType||'General').toLowerCase().replace(' ','-')}`}>
                                  {note.noteType||'General'}
                                </span>
                                <span className="opp-note-title">{note.title}</span>
                              </div>
                              <div className="opp-note-actions">
                                <span className="opp-note-date">
                                  {note.createdAt ? new Date(note.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                                </span>
                                <button className="opp-note-delete" onClick={()=>handleDeleteNote(note.id)} title="Delete note">×</button>
                              </div>
                            </div>
                            <div className="opp-note-content">{note.content}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Tasks ────────────────────────────────────────────── */}
                  <div className="opp-ws-subsection">
                    <div className="opp-ws-section-header">
                      <h4>Tasks ({(detailData?.tasks||[]).length})</h4>
                      <button className="opp-ws-add-btn" onClick={()=>setShowAddTask(true)}>+ Add Task</button>
                    </div>

                    {/* Add task form */}
                    {showAddTask&&(
                      <div className="opp-add-note-form">
                        <input className="opp-note-title-input" value={newTaskName}
                          onChange={e=>setNewTaskName(e.target.value)}
                          placeholder="Task name *" autoFocus/>
                        <div className="opp-note-form-row">
                          <select className="opp-note-type-select" value={newTaskPriority}
                            onChange={e=>setNewTaskPriority(e.target.value)}>
                            <option>Do First</option>
                            <option>Do Today</option>
                            <option>This Week</option>
                          </select>
                          <input type="date" className="opp-note-type-select" value={newTaskDue}
                            onChange={e=>setNewTaskDue(e.target.value)}/>
                        </div>
                        <div className="opp-note-form-actions">
                          <button className="opp-btn-secondary" onClick={()=>{setShowAddTask(false);setNewTaskName('');}}>Cancel</button>
                          <button className="opp-btn-primary" onClick={handleCreateTask}
                            disabled={savingTask||!newTaskName.trim()}>
                            {savingTask?'Creating...':'Create Task'}
                          </button>
                        </div>
                      </div>
                    )}

                    {detailLoading?<div className="opp-ws-loading">Loading...</div>:
                    (detailData?.tasks||[]).length===0&&!showAddTask?(
                      <div className="opp-ws-empty"><p>No tasks yet for this pursuit.</p></div>
                    ):(
                      <div className="opp-task-list">
                        {(detailData?.tasks||[]).map(t=>(
                          <div key={t.id} className={`opp-task-item ${(t.status||'').toLowerCase()}`}>
                            <div className="opp-task-status">{t.status==='Done'?'✅':t.status==='In Progress'?'🔄':t.priority==='Do First'?'🔴':'📋'}</div>
                            <div className="opp-task-info">
                              <div className="opp-task-name">{t.name}</div>
                              {t.dueDate&&<div className="opp-task-due">Due: {t.dueDate}</div>}
                            </div>
                            <div className="opp-task-priority" style={{color:t.priority==='Do First'?'var(--dm-red)':t.priority==='Do Today'?'#F97316':'var(--dm-grey-600)'}}>
                              {t.priority}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Activities ───────────────────────────────────────── */}
                  <div className="opp-ws-subsection">
                    <div className="opp-ws-section-header">
                      <h4>Recent Activities ({(detailData?.activities||[]).length})</h4>
                      <button className="opp-ws-add-btn" onClick={()=>setShowLogModal(true)}>+ Log Activity</button>
                    </div>


                    {(detailData?.activities||[]).length === 0 ? (
                      <div className="opp-ws-empty">
                        <p>No activities logged yet.</p>
                        <button className="opp-ws-add-btn-lg" onClick={()=>setShowLogModal(true)}>+ Log First Activity</button>
                      </div>
                    ) : (
                      <div className="opp-activity-list">
                        {(detailData?.activities||[]).slice(0,10).map(a=>(
                          <div key={a.id} className="opp-activity-item">
                            <div className="opp-activity-type">{a.activityType||'📌'}</div>
                            <div className="opp-activity-info">
                              <div className="opp-activity-name">{a.name}</div>
                              {a.activityDate&&<div className="opp-activity-date">{new Date(a.activityDate && a.activityDate.includes('T') ? a.activityDate : (a.activityDate||'')+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>}
                              {a.outcome&&<div className="opp-activity-outcome">{a.outcome}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── VIEW: AI WAR ROOM TAB ─────────────────────────────────── */}
              {modalMode==='view'&&modalTab==='ai'&&(
                <div className="opp-ai-warroom">
                  <AIPursuitChat
                    ref={chatRef}
                    context={{
                      type:            'opportunity',
                      opportunityId:   selectedOpp?.id,
                      opportunityName: selectedOpp?.name,
                      accountId,
                      accountName,
                      stage:           selectedOpp?.probability,
                      dealSize:        selectedOpp?.dealSize,
                      contacts:        detailContacts || [],
                      notes:           notes,
                    }}
                    openingBriefing={buildOpeningBriefing(selectedOpp, detailData)}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="opp-modal-footer">
              {modalMode==='view'?(
                <>
                  <button
                    className="opp-btn-primary"
                    disabled={endingSession}
                    onClick={async ()=>{
                      if (!selectedOpp) return;
                      setEndingSession(true);
                      setEndSessionStatus('💾 Saving session...');
                      try {
                        const result = chatRef.current
                          ? await chatRef.current.endSession()
                          : { status: 'empty' };
                        if (result.status === 'saved') {
                          setEndSessionStatus('✓ Saved & distilled');
                          await new Promise(r=>setTimeout(r, 600));
                        } else if (result.status === 'empty') {
                          // No chat content — close immediately, no LLM call needed
                        } else {
                          setEndSessionStatus('⚠️ Save issue — closing anyway');
                          console.warn('[End Session]', result);
                          await new Promise(r=>setTimeout(r, 1200));
                        }
                        handleCloseModal();
                      } finally {
                        setEndingSession(false);
                        setEndSessionStatus('');
                      }
                    }}>
                    {endingSession ? (endSessionStatus || '⏳ Working...') : '💾 End Session'}
                  </button>
                  {selectedOpp && CLOSED_STAGES.includes(selectedOpp.probability) && (
                    <button className="opp-btn-secondary" onClick={()=>{
                      if (!selectedOpp) return;
                      setDebriefOpp({name: selectedOpp.name, notionUrl: selectedOpp.notionUrl || ''});
                      handleCloseModal();
                    }}>🧠 Debrief</button>
                  )}
                  <button className="opp-btn-danger" onClick={()=>setShowDeleteConfirm(true)}>Delete</button>
                </>
              ):(
                <>
                  <button className="opp-btn-secondary" onClick={handleCloseModal}>Cancel</button>
                  <button className="opp-btn-primary" onClick={handleSave} disabled={saving||!formData.name.trim()}>
                    {saving?'⏳ Saving...':modalMode==='add'?'Create':'Save'}
                  </button>
                </>
              )}
            </div>

            {showDeleteConfirm&&(
              <div className="opp-confirm-overlay">
                <div className="opp-confirm-box">
                  <h3>Delete Opportunity?</h3>
                  <p>Permanently delete <strong>"{selectedOpp?.name}"</strong>?</p>
                  <div className="opp-confirm-actions">
                    <button className="opp-btn-secondary" onClick={()=>setShowDeleteConfirm(false)}>Cancel</button>
                    <button className="opp-btn-danger" onClick={handleDelete} disabled={deleting}>
                      {deleting?'⏳ Deleting...':'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showLogModal && selectedOpp && (
        <LogActivityModal
          accountId={accountId}
          accountName={accountName}
          opportunityId={selectedOpp.id}
          opportunityName={selectedOpp.name}
          onClose={() => setShowLogModal(false)}
          onSaved={async (result) => {
            setShowLogModal(false);
            setDetailData(null);
            loadDetail(selectedOpp.id);
            const parts = ['✅ Activity logged!'];
            if (result.taskId)       parts.push('Task created.');
            if (result.cyranoQueued) parts.push('Draft sent to Staging.');
            if (result.calendarId)   parts.push('Meeting added to calendar.');
            showToast(parts.join(' '));
          }}
        />
      )}

      {debriefOpp&&(
        <VictorDebrief
          opportunityName={debriefOpp.name}
          accountName={accountName}
          opportunityNotionUrl={debriefOpp.notionUrl}
          onClose={()=>setDebriefOpp(null)}
          onComplete={()=>{setDebriefOpp(null);showToast('🧠 Debrief captured!');}}
        />
      )}
    </div>
  );
}

export default OpportunitiesTab;
